import { addDays, isAfter, isBefore, isEqual, parse } from "date-fns";
import { type DateComparisonType } from "../../types";
import { FieldValidationParams } from "../types";
import { validationException } from "./validator.utils";

const CONTROL_NAME = "datesComparison";

// evaluate all functions with OR condition.
const operatorsMap = {
  gt: [isAfter],
  gte: [isAfter, isEqual],
  lte: [isBefore, isEqual],
  lt: [isBefore],
  eq: [isEqual],
};

export const validateDatesComparison = async ({
  args,
  names,
  onValidated,
  onInvalid,
  onValidationStarted,
}: FieldValidationParams<DateComparisonType>) => {
  return new Promise((resolve, reject) => {
    const [ruleDefinition, value, _, schema] = args;
    const { instancePath = "", parentData } = schema;
    const paths = instancePath.split("/");
    const name = paths[paths.length - 1];
    const dateFormat = (parentData.date as string) || "dd/MM/yyyy";
    if (onValidationStarted) {
      onValidationStarted(CONTROL_NAME, name);
    }
    const checkValidated = () => {
      if (onValidated) {
        onValidated(names, name, CONTROL_NAME, []);
      }
    };

    const checkInvalid = (errorCode = 0) => {
      if (onInvalid) {
        onInvalid(names, name, CONTROL_NAME, errorCode, []);
      }
    };

    const { rules, rulesConjunction = "and" } = ruleDefinition;

    const now = new Date();
    const valueToDate = parse(value, dateFormat, now);
    const operationsResult = rules.map(rule => {
      const { ref, operator, daysOffset = 0, bypassIfEmpty = false } = rule;
      const comparisonValue = parentData[ref] as string;
      if (bypassIfEmpty && !comparisonValue) {
        return true;
      }

      const comparisonOperator = operator as keyof typeof operatorsMap;
      const operators = operatorsMap[comparisonOperator];
      const comparisonValueToDate = parse(comparisonValue, dateFormat, now);
      const comparisonValueToDateWithOffset = addDays(
        comparisonValueToDate,
        daysOffset,
      );
      const match = operators.some(operation =>
        operation(valueToDate, comparisonValueToDateWithOffset),
      );
      return match;
    });

    let isValid = operationsResult.every(r => r);
    if (rulesConjunction === "or") {
      isValid = operationsResult.some(r => r);
    }

    if (!isValid) {
      checkInvalid();
      return reject(
        validationException(CONTROL_NAME, "Date comparison failed"),
      );
    }
    checkValidated();
    return resolve(true);
  });
};
